import { getRewardfulReferral } from "../../utils/rewardful";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { axiosInstance } from "../../config";
import "./PricingPage.css";

// New Opus-mirrored pricing: 1 credit = 1 minute of source video processed.
// Matches Opus ($15/$29) — Starter $15 / Pro $29. Annual is
// 50% off the monthly rate. Free trial: 7 days, card required up-front,
// auto-charges Starter on day 8 unless cancelled.
const PLANS = [
  {
    key: "Starter",
    label: "Starter",
    desc: "For individual creators getting started",
    monthlyPrice: 15,
    annualPrice: null,         // monthly only, as on OpusClip
    credits: 150,
    highlight: false,
    features: [
      "150 credits/mo (≈150 min of source video)",
      "9:16 + 16:9 aspect ratios",
      "HD rendered clips",
      "AI animated captions",
      "Watermark-free exports",
    ],
  },
  {
    key: "Pro",
    label: "Pro",
    desc: "For professional creators publishing daily",
    monthlyPrice: 29,
    annualPrice: 14.5,           // $14.50/mo = $174/yr
    credits: 300,
    highlight: true,
    badge: "MOST POPULAR",
    features: [
      "300 credits/mo (≈300 min of source video)",
      "9:16 + 16:9 aspect ratios",
      "HD rendered clips",
      "AI animated captions",
      "Watermark-free exports",
    ],
  },
  {
    key: "Premium",
    label: "Premium",
    desc: "For power creators scaling to the max",
    monthlyPrice: 145,
    annualPrice: 72.5,           // $72.50/mo = $870/yr (50% off)
    credits: 1500,
    highlight: false,
    badge: "MOST CREDITS",
    features: [
      "1,500 credits/mo (≈25 hours of source video)",
      "9:16 + 16:9 aspect ratios",
      "HD rendered clips",
      "AI animated captions",
      "Watermark-free exports",
      "Most credits for heavy clippers",
    ],
  },
  {
    key: "Business",
    label: "Business",
    desc: "For organizations needing tailored solutions",
    monthlyPrice: null,         // Custom
    annualPrice: null,
    credits: null,
    highlight: false,
    isCustom: true,
    features: [
      "Everything in Pro",
      "Priority project processing",
      "Custom credit packs, team seats, social accounts",
      "Tailored brand assets, fonts, vocabulary",
      "Dedicated storage",
      "API & custom integrations",
      "Enterprise support (dedicated Slack)",
    ],
  },
];

const CREDIT_PACKS = [
  { id: "pack_100", credits: 100, price: 90,  originalPrice: 120,  badge: "Best Value",   mostPopular: false, featured: true,  desc: "100 extra video generations" },
  { id: "pack_40",  credits: 40,  price: 44,  originalPrice: 60,   badge: "Most Popular", mostPopular: true,  featured: false, desc: "40 extra video generations" },
  { id: "pack_15",  credits: 15,  price: 20,  originalPrice: 24,   badge: "Save 16%",     mostPopular: false, featured: false, desc: "15 extra video generations" },
  { id: "pack_5",   credits: 5,   price: 8,   originalPrice: null, badge: null,           mostPopular: false, featured: false, desc: "5 extra video generations" },
];

const CheckIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const PricingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") === "credits" ? "credits" : "plans");
  const [billingInterval, setBillingInterval] = useState("monthly");
  const [loading, setLoading] = useState(null);

  const isAnnual = billingInterval === "annual";

  // Phase 1 of pricing transition: UI shows new $12/$24 prices but Stripe
  // products still bill at the old $20/$81/$249 rates. Until Phase 2 (when
  // new Stripe products are created in dashboard and REACT_APP_NEW_PRICING_LIVE=true),
  // route signup clicks to contact email to prevent accidental overcharging.
  const NEW_PRICING_LIVE = process.env.REACT_APP_NEW_PRICING_LIVE === "true";

  const handleSubscribe = async (planKey) => {
    if (!NEW_PRICING_LIVE) {
      window.open(
        "mailto:clipbaitla@gmail.com?subject=" +
          encodeURIComponent(`Early access — ${planKey} plan`),
        "_blank"
      );
      return;
    }
    setLoading(planKey);
    try {
      const referral = getRewardfulReferral();
      const res = await axiosInstance.post("/billing/plan/checkout", {
        planName: planKey,
        // A plan with no yearly price (Starter) always checks out monthly.
        interval: PLANS.find((p) => p.key === planKey)?.annualPrice ? billingInterval : "monthly",
        ...(referral ? { referral } : {}),
      });
      if (res.data?.url) window.location.href = res.data.url;
    } catch (err) {
      console.error("Checkout error:", err);
      setLoading(null);
    }
  };

  const handleBuyCredits = async (packId) => {
    setLoading(packId);
    try {
      const res = await axiosInstance.post("/billing/credits/checkout", { packId });
      if (res.data?.url) window.location.href = res.data.url;
    } catch (err) {
      console.error("Credits checkout error:", err);
      setLoading(null);
    }
  };

  return (
    <div className="pricing-page">
      {/* Back button */}
      <button className="pricing-back-btn" onClick={() => navigate(-1)}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <polyline points="15 18 9 12 15 6" />
        </svg>
        Back
      </button>

      <div className="pricing-inner">
        {/* Header */}
        <div className="pricing-header">
          <div className="pricing-eyebrow">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
            </svg>
            BUILT FOR POWER CREATORS
          </div>
          <h1 className="pricing-title">
            GENERATE <span className="pricing-title-accent">WITHOUT</span><br />
            LIMITS
          </h1>
          <p className="pricing-subtitle">
            Upgrade your plan or top up credits to keep creating without interruptions.
          </p>
        </div>

        {/* Tabs */}
        <div className="pricing-tabs">
          <button
            className={`pricing-tab${tab === "plans" ? " active" : ""}`}
            onClick={() => setTab("plans")}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
            </svg>
            Upgrade Plan
          </button>
          <button
            className={`pricing-tab${tab === "credits" ? " active" : ""}`}
            onClick={() => setTab("credits")}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            Top-up Credits
          </button>
        </div>

        {tab === "plans" && (
          <>
            {/* Billing toggle */}
            <div className="pricing-interval-row">
              <button
                className={`pricing-interval-btn${!isAnnual ? " active" : ""}`}
                onClick={() => setBillingInterval("monthly")}
              >
                Monthly
              </button>
              <button
                className={`pricing-interval-btn${isAnnual ? " active" : ""}`}
                onClick={() => setBillingInterval("annual")}
              >
                Annual
              </button>
              <span className="pricing-interval-save">SAVE 50%</span>
            </div>

            {/* Plan cards */}
            <div className="pricing-plans-grid">
              {PLANS.map((plan) => {
                // Starter has no yearly price: on Annual it keeps its monthly price.
                const monthlyOnly = isAnnual && !plan.isCustom && !plan.annualPrice;
                const showAnnual = isAnnual && !monthlyOnly;
                const price = showAnnual ? plan.annualPrice : plan.monthlyPrice;
                const originalPrice = showAnnual ? plan.monthlyPrice : null;
                const isCustom = plan.isCustom;
                return (
                  <div
                    key={plan.key}
                    className={`pricing-plan-card${plan.highlight ? " featured" : ""}${plan.badge === "MOST CREDITS" ? " best-value" : ""}`}
                  >
                    {plan.badge && (
                      <div className={`pricing-plan-banner${plan.badge === "MOST CREDITS" ? " best-value" : " most-popular"}`}>
                        {plan.badge === "MOST POPULAR" ? (
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                          </svg>
                        ) : (
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                          </svg>
                        )}
                        {plan.badge}
                      </div>
                    )}

                    <div className="pricing-plan-label">{plan.label}</div>
                    <div className="pricing-plan-desc">{plan.desc}</div>

                    {isCustom ? (
                      <div className="pricing-plan-price-row">
                        <span className="pricing-plan-amount">Custom</span>
                      </div>
                    ) : (
                      <>
                        <div className="pricing-plan-price-row">
                          {originalPrice && (
                            <span className="pricing-plan-original">${originalPrice}</span>
                          )}
                          <span className="pricing-plan-amount">${Number.isInteger(price) ? price : price.toFixed(2)}</span>
                          <span className="pricing-plan-period">/month</span>
                        </div>
                        {showAnnual && (
                          <div className="pricing-plan-billed">Billed annually (${price * 12}/yr)</div>
                        )}
                        {monthlyOnly && (
                          <div className="pricing-plan-billed">Starter plan only available in monthly</div>
                        )}
                      </>
                    )}

                    {plan.credits && (
                      <div className="pricing-plan-gens-box">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                        </svg>
                        {plan.credits} credits/mo · {plan.credits} min of video
                      </div>
                    )}

                    <button
                      className={`pricing-plan-btn${plan.highlight ? " primary" : ""}`}
                      onClick={() => isCustom ? window.open("mailto:clipbaitla@gmail.com?subject=Business plan inquiry", "_blank") : handleSubscribe(plan.key)}
                      disabled={loading === plan.key}
                    >
                      {loading === plan.key ? (
                        <span className="pricing-spinner" />
                      ) : isCustom
                          ? "Contact us"
                          : NEW_PRICING_LIVE
                            ? "Subscribe"
                            : "Request early access"}
                    </button>
                    {!isCustom && (
                      <div className="pricing-plan-trial-note">
                        {NEW_PRICING_LIVE
                          ? "Auto-renews · cancel anytime"
                          : "Launching soon — email for early access"}
                      </div>
                    )}

                    <ul className="pricing-plan-features">
                      {plan.features.map((f) => (
                        <li key={f}>
                          <CheckIcon />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>

            <p className="pricing-footer-note">
              1 credit = 1 minute of source video processed. Prices exclude VAT.
              Cancel anytime.
            </p>
          </>
        )}

        {tab === "credits" && (
          <div className="pricing-credits-section">
            <p className="pricing-credits-note">
              Credits never expire and stack on top of your monthly plan allowance.
            </p>
            <div className="pricing-credit-packs">
              {CREDIT_PACKS.map((pack) => {
                const discount = pack.originalPrice
                  ? Math.round((1 - pack.price / pack.originalPrice) * 100)
                  : null;
                return (
                  <div
                    key={pack.id}
                    className={`pricing-credit-pack${pack.featured ? " featured" : ""}${pack.mostPopular ? " most-popular-pack" : ""}`}
                  >
                    {pack.mostPopular && (
                      <div className="pricing-pack-glow" />
                    )}
                    <div className="pricing-credit-pack-left">
                      <div className="pricing-credit-pack-credits">
                        <span className="pricing-credit-count">{pack.credits}</span>
                        <span className="pricing-credit-word"> credits</span>
                        {pack.badge && (
                          <span className={`pricing-credit-badge${pack.featured ? " best" : pack.mostPopular ? " popular" : " save"}`}>
                            {pack.badge}
                          </span>
                        )}
                        {discount && (
                          <span className="pricing-credit-discount-pill">-{discount}%</span>
                        )}
                      </div>
                      <div className="pricing-credit-pack-desc">{pack.desc}</div>
                    </div>

                    <div className="pricing-credit-pack-right">
                      <div className="pricing-credit-price-row">
                        {pack.originalPrice && (
                          <span className="pricing-credit-original">${pack.originalPrice}</span>
                        )}
                        <span className="pricing-credit-price">${pack.price}</span>
                      </div>
                      <button
                        className={`pricing-credit-btn${pack.featured ? " pricing-credit-btn--featured" : ""}`}
                        onClick={() => handleBuyCredits(pack.id)}
                        disabled={loading === pack.id}
                      >
                        {loading === pack.id ? <span className={`pricing-spinner${pack.featured ? " pricing-spinner--dark" : ""}`} /> : "Purchase"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PricingPage;
